/**
 * Minimal document.title + meta description manager that reads the metaInfo()
 * component option. Replaces vue-meta for this app's usage.
 *
 * Components declare:
 *   metaInfo() { return { title: 'Page Name' }; }
 *
 * Pages may append a trailing segment and set the meta description:
 *   metaInfo() {
 *     return {
 *       title: 'Menu',
 *       titleSuffix: category.displayName,          // -> `${builtTitle} | ${suffix}`
 *       description: category.description            // -> <meta name="description">
 *     };
 *   }
 *
 * Layout/wrapper components declare:
 *   metaInfo() { return { titleTemplate(chunk) { return `${chunk} | ${merchant.name}`; } }; }
 *
 * When a page component with title mounts or the route changes, this plugin
 * walks up the $parent chain to find the nearest titleTemplate ancestor and
 * applies it. Pages whose data loads asynchronously can call `this.$applyMeta()`
 * once the data is ready to re-run this logic.
 *
 * The initial <meta name="description"> content in index.html is captured as the
 * default and restored whenever a titled page does not declare its own.
 */

let defaultDescription = '';

function getComponentMeta(vm) {
  const { metaInfo } = vm.$options;
  if (!metaInfo) return null;
  return typeof metaInfo === 'function' ? metaInfo.call(vm) : metaInfo;
}

function findTitleTemplate(vm) {
  let current = vm;

  while (current) {
    const meta = getComponentMeta(current);
    if (meta && meta.titleTemplate) return meta.titleTemplate;
    current = current.$parent || null;
  }

  return null;
}

function applyDescription(content) {
  const head = document.head || document.getElementsByTagName('head')[0];

  let metaDescription = document.querySelector('meta[name="description"]');

  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    head.appendChild(metaDescription);
  }

  metaDescription.setAttribute('content', content);
}

function applyMetaInfo(vm) {
  const meta = getComponentMeta(vm);
  if (!meta || !meta.title) return;

  const titleTemplate = findTitleTemplate(vm.$parent);

  let title = titleTemplate ? titleTemplate(meta.title) : meta.title;
  if (meta.titleSuffix) title = `${title} | ${meta.titleSuffix}`;

  document.title = title;

  applyDescription(meta.description || defaultDescription);
}

export default {
  install(app) {
    const initialDescription = document.querySelector('meta[name="description"]');
    defaultDescription = initialDescription ? initialDescription.getAttribute('content') || '' : '';

    app.config.globalProperties.$applyMeta = function applyMeta() {
      applyMetaInfo(this);
    };

    app.mixin({

      watch: {
        $route: {
          handler() {
            applyMetaInfo(this);
          }
        }
      },
      mounted() {
        applyMetaInfo(this);
      }
    });
  }
};
